import { Moon, Sun } from "lucide-react"
import type { MouseEvent } from "react"
import { useAppearance } from "@/hooks/use-appearance"
import { cn } from "@/lib/utils"

type ViewTransitionLike = {
  ready: Promise<void>
}

type ViewTransitionDocument = Document & {
  startViewTransition?: (callback: () => void) => ViewTransitionLike
}

export function AnimatedThemeToggler({ className }: { className?: string }) {
  const { resolvedAppearance, updateAppearance } = useAppearance()
  const nextAppearance = resolvedAppearance === "dark" ? "light" : "dark"
  const label = resolvedAppearance === "dark" ? "Switch to light mode" : "Switch to dark mode"

  const toggleTheme = async (event: MouseEvent<HTMLButtonElement>) => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const transitionDocument = document as ViewTransitionDocument

    if (reduceMotion || !transitionDocument.startViewTransition) {
      updateAppearance(nextAppearance)

      return
    }

    const { clientX, clientY } = event
    const radius = Math.hypot(
      Math.max(clientX, window.innerWidth - clientX),
      Math.max(clientY, window.innerHeight - clientY),
    )
    const transition = transitionDocument.startViewTransition(() => {
      updateAppearance(nextAppearance)
    })

    await transition.ready

    document.documentElement.animate(
      {
        clipPath: [
          `circle(0px at ${clientX}px ${clientY}px)`,
          `circle(${radius}px at ${clientX}px ${clientY}px)`,
        ],
      },
      {
        duration: 400,
        easing: "cubic-bezier(0.16, 1, 0.3, 1)",
        pseudoElement: "::view-transition-new(root)",
      } as KeyframeAnimationOptions & { pseudoElement: string },
    )
  }

  return (
    <button
      type="button"
      className={cn("animated-theme-toggle", className)}
      aria-label={label}
      title={label}
      onClick={toggleTheme}
    >
      {resolvedAppearance === "dark" ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
    </button>
  )
}
